import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
// import SkeletonVendorPage from '../LoadingPages/SkeletonVendorPage.jsx';
import { kaReducer, Table } from 'ka-table';
import { DataType, SortingMode } from 'ka-table/enums';
import AdminPanelForm from './AdminPanelForm';
import { AdminPanelContext } from '../../../Context/AdminPanelContext';
import DeleteConfirmUserComponent from './DeleteConfirmUserComponent';
import { GlobalSearch } from '../GlobalSearch';
import { search } from 'ka-table/actionCreators';

// import css from "../style.module.css"



export default function AdminTable({ usersList }) {
    const { deleteMarketingUsers, setError } = useContext(AdminPanelContext);

    const [searchText, setSearchText] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [formAction, setFormAction] = useState(null); // 'update' أو 'delete'
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDeleteConfirm, setUserDeleteConfirm] = useState(false);
    const [user, setUser] = useState(null);

    // 🎯 NEW: Track which rows are being deleted
    const [deletingRows, setDeletingRows] = useState(new Set());

    const [tableProps, setTableProps] = useState({
        columns: [
            { key: 'rowIndex', title: '#', dataType: DataType.Number },
            { key: 'name', title: 'Name', dataType: DataType.String },
            { key: 'email', title: 'Email', dataType: DataType.String },
            { key: 'role', title: 'Role', dataType: DataType.String },
            { key: 'active', title: 'Status', dataType: DataType.Boolean },
            { key: 'actions', title: 'Actions', dataType: DataType.String }
        ],
        data: [],
        rowKeyField: 'id',
        sortingMode: SortingMode.Single,
        paging: {
            enabled: true,
            pageIndex: 0,
            pageSize: 10,
            pageSizes: [10, 20, 30],
        },
    });

    const roleLabels = {
        admin: "Admin",
        marketing: "Marketing",
        compliance: "Compliance",
        baseUser: "Base User",
    };

    // Memoize columns to prevent unnecessary recalculations
    const columns = useMemo(() => tableProps.columns, [tableProps.columns]);

    // Load and format users
    useEffect(() => {
        if (Array.isArray(usersList)) {
            const formatted = usersList.map((user, idx) => ({
                id: user.id,
                rowIndex: idx + 1,
                name: user.name,
                email: user.email,
                role: user.role,
                active: user.active,
                rawData: user,
            }));
            setFilteredData(formatted);
            setTableProps((prev) => ({ ...prev, data: formatted }));
        }
    }, [usersList]);

    // Handle search input change

    const handleGlobalSearch = (value) => {
        dispatch(search(value));
    };


    // Filter data when searchText or filteredData changes
    useEffect(() => {
        if (!filteredData.length) return;

        if (!searchText.trim()) {
            setTableProps((prev) => ({ ...prev, data: filteredData }));
        } else {
            const term = searchText.toLowerCase().trim();
            const results = filteredData
                .filter((item) =>
                    columns.some((col) => {
                        const value = item[col.key];
                        return value != null && String(value).toLowerCase().includes(term);
                    })
                )
                .map((item, idx) => ({ ...item, rowIndex: idx + 1 }));
            setTableProps((prev) => ({ ...prev, data: results }));
        }
    }, [searchText, filteredData, columns]);

    // Ka-Table dispatch
    const dispatch = (action) => setTableProps((prev) => kaReducer(prev, action));

    // Empty data placeholder
    const EmptyDataComponent = () => (
        <tr>
            <td colSpan={columns.length} className="text-center py-3">
                No matching records found
            </td>
        </tr>
    );

    const handleAdd = () => {
        setFormAction('add');
    };

    const handleEdit = (user) => {
        setFormAction('update');
        setSelectedUser(user);
    };

    // 🎯 ENHANCED: Delete function with row-specific loading
    const handleConfirmDelete = (user) => {
        setUserDeleteConfirm(true);
        setUser(user);
    }
    const closeForm = () => {
        setError(null);
        setFormAction(null);
        setSelectedUser(null);
        setUserDeleteConfirm(false);
    };

    const handleDelete = async (user) => {
        try {
            setDeletingRows(prev => new Set([...prev, user.id]));
            const result = await deleteMarketingUsers(user.id);

            if (result.success) {
                setFilteredData(prev => {
                    const next = prev.filter(item => item.id !== user.id);
                    dispatch({ type: 'UpdateData', data: next });
                    return next;
                });
            }
        } catch (error) {
            console.error('Error deleting user:', error);

        } finally {
            setDeletingRows(prev => {
                const newSet = new Set(prev);
                newSet.delete(user.id);
                return newSet;
            });
            closeForm();
        }
    };





    return (
        <div className="ms-1 p-0">
            <div className='d-flex justify-content-between align-items-center'>
                <div style={{ margin: '5px 0 7px' }}>
                    <GlobalSearch onSearchChange={handleGlobalSearch} />
                </div>
                <button className='btn btn-outline-primary' onClick={() => handleAdd()}>
                    Add Internal User
                </button>
            </div>
            {formAction && (
                <AdminPanelForm
                    action={formAction}
                    user={selectedUser ? selectedUser : ""}
                    onClose={closeForm}
                />
            )}
            {userDeleteConfirm && (
                <DeleteConfirmUserComponent
                    handleDelete={handleDelete}
                    user={user}
                    onClose={closeForm}
                    isDeleting={deletingRows.has(user.id)}
                />
            )}
            {tableProps.data.length > 0 ? (
                <>
                    <Table
                        {...tableProps}
                        dispatch={dispatch}
                        childComponents={{
                            cellText: {
                                content: ({ column, rowData, value }) => {
                                    // 🎯 Check if this row is being deleted
                                    const isDeleting = deletingRows.has(rowData.id);

                                    if (column.key === 'actions') {
                                        return (
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleEdit(rowData)}
                                                    disabled={isDeleting} // Disable edit when deleting
                                                >
                                                    <i className="fa fa-edit"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => handleConfirmDelete(rowData)}
                                                    disabled={isDeleting} // Disable delete button when deleting
                                                >
                                                    {isDeleting ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                    ) : (
                                                        <i className="fa fa-trash"></i>
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    }
                                    if (column.key === 'active') {
                                        const displayValue = (value === true || value === "Active" || value === "true") ? "Active" : "Inactive";
                                        const statusClass = (value === true || value === "Active" || value === "true") ? 'text-success' : 'text-danger';
                                        return <span className={`${statusClass} fw-bold`}>{displayValue}</span>;
                                    }

                                    if (column.key === 'role') {
                                        return <span style={isDeleting ? { opacity: 0.5, color: 'var(--ink-soft)' } : {}}>{roleLabels[value] || value}</span>;
                                    }

                                    // 🎯 Apply disabled styling to cell content when deleting
                                    const cellStyle = isDeleting ? { opacity: 0.5, color: 'var(--ink-soft)' } : {};
                                    return <span style={cellStyle}>{value}</span>;
                                }
                            },
                            table: { elementAttributes: () => ({ className: 'rounded-table ' }) },
                            tableHead: { elementAttributes: () => ({ className: 'table-header' }) },
                            headCell: { elementAttributes: () => ({ className: 'py-1 px-2' }) },
                            // 🎯 Apply disabled styling to entire row when deleting
                            dataRow: {
                                elementAttributes: ({ rowData }) => {
                                    const isDeleting = deletingRows.has(rowData.id);
                                    return {
                                        className: `table-row ${isDeleting ? 'table-row-deleting' : ''}`,
                                        style: isDeleting ? {
                                            backgroundColor: 'var(--surface-sunken)',
                                            opacity: 0.7,
                                            pointerEvents: 'none'
                                        } : {}
                                    };
                                }
                            },
                            cell: { elementAttributes: () => ({ className: 'py-1 px-2' }) },

                            // Paging Controls
                            paging: {
                                elementAttributes: () => ({
                                    className: 'custom-paging'
                                })
                            },
                            pagingPages: {
                                elementAttributes: () => ({
                                    className: 'd-flex justify-content-end'
                                })
                            },
                            pagingIndex: {
                                elementAttributes: props => ({
                                    className: `pagination-item ${props.isActive ? 'active' : ''}`
                                })
                            },
                            pagingSizes: {
                                content: ({ dispatch, pageSize }) => (
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">Rows per page:</span>
                                        <input
                                            type="number"
                                            value={pageSize}
                                            min={1}
                                            onChange={(e) => {
                                                const size = Number(e.target.value) || 1;
                                                dispatch({ type: 'UpdatePageSize', pageSize: size });
                                            }}
                                            style={{ width: '60px', textAlign: 'center' }}
                                        />
                                    </div>
                                )
                            }
                        }}
                    />
                </>
            ) : (
                <table className="rounded-table">
                    <thead>
                        <tr>
                            <th className="py-1 px-2">#</th>
                            <th className="py-1 px-2">Name</th>
                            <th className="py-1 px-2">Email</th>
                            <th className="py-1 px-2">Role</th>
                            <th className="py-1 px-2">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <EmptyDataComponent />
                    </tbody>
                </table>
            )}
        </div>
    );
}
